import { useEffect, useRef } from "react";

/**
 * Scrolls the returned ref's element into view whenever `deps` change: an instant jump the
 * first time the element is there, a smooth scroll afterwards.
 *
 * A run without the element (the caller rendered a loading state instead) does not use up
 * the instant jump, so put the loading flag in `deps` to scroll once the element mounts
 * (CR-171: the chat opened at the top after its attachments spinner).
 */
export function useScrollTo<T extends HTMLElement>(deps: unknown[]) {
  const bottomRef = useRef<T | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!bottomRef.current) {
      return;
    }
    bottomRef.current.scrollIntoView({
      behavior: isFirstRender.current ? "auto" : "smooth",
    });

    isFirstRender.current = false;
  }, deps);

  return bottomRef;
}
